import { Button, Center, Stack, Text, Title } from '@mantine/core';
import { AxiosError } from 'axios';
import { Link, useParams } from 'react-router-dom';

import { useGetPageReport } from '../api/getReport';
import { ReportCard } from '../components/ReportCard';

import { ListLoader, ServerError } from '@/components';
import { isValidObjectId } from '@/utils';

// 403 is treated like 404: once access is lost, the already loaded report must not stay on
// screen, and the «not found» copy already covers «no access» (DEV-3947).
function isNotFoundError(error: unknown): boolean {
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  return status === 404 || status === 403;
}

const ReportNotFound = () => (
  <Center h="50vh">
    <Stack align="center" gap="md">
      <Title order={3}>Отчёт не найден</Title>
      <Text c="dimmed" ta="center" maw={360}>
        Отчёт не существует или у вас нет доступа к нему
      </Text>
      <Button component={Link} to="/ai-reports" variant="light">
        К списку отчётов
      </Button>
    </Stack>
  </Center>
);

export const AiReportPage = () => {
  const { reportId = '' } = useParams();
  const isValidId = isValidObjectId(reportId);
  const reportQuery = useGetPageReport({ reportId });

  // Not an ObjectId (e.g. /ai-reports/types): not found without calling the API (DEV-3961).
  if (!isValidId) return <ReportNotFound />;

  if (reportQuery.isLoading) return <ListLoader />;

  if (reportQuery.isError && isNotFoundError(reportQuery.error)) return <ReportNotFound />;

  // A failed background poll keeps the loaded card on screen; the full error state
  // is only for a failed initial load (DEV-3947).
  if (!reportQuery.data) {
    return (
      <ServerError onRetry={() => reportQuery.refetch()} isFetching={reportQuery.isFetching} />
    );
  }

  const { report, batch } = reportQuery.data;
  // Unexpected response shape must not crash the app (DEV-3961).
  if (!report || typeof report !== 'object') return <ReportNotFound />;

  return (
    <Stack p="md">
      {reportQuery.isError && (
        <Text size="sm" c="red">
          Не удалось обновить статус — повторяем…
        </Text>
      )}
      <ReportCard
        report={report}
        batch={Array.isArray(batch) ? batch : []}
        receivedAt={reportQuery.dataUpdatedAt}
      />
    </Stack>
  );
};
